import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";

import { logIn, register, resetpass } from "../../../api/Auth";
import { searchAddress } from "../../../api/Store";
import { useVehicleReservation } from "../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import { useStoreDetails } from "../../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { VehicleReservationMainProps } from "../../../common/Providers/VehicleReservationProvider/VehicleReservationProvider";
import LogInAs from "../../../common/LogInAs";
import RentalHeader from "../../../components/rental/RentalHeader";
import StepperLayout from "./StepperLayout";
import SummaryCard from "../SummaryCard";
import { useStepper } from "../../../common/Providers/StepperProvider/useStepper";
import FloatingActionButton from "../../FloatingActionButton";
import { useManageKeys } from "../../../common/Providers/ManageKeysProvider/UseManageKeys";
import Editable from "../../../cms/Editable";

interface InfoStepProps {
  sx?: any;
  addressError?: boolean;
}

type FormMode = "signin" | "signup" | "reset";

type SignUpFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  smsOptIn: boolean;
};

type SignInFormValues = {
  email: string;
  password: string;
};

type FormValidation = {
  firstName: boolean | null;
  lastName: boolean | null;
  email: boolean | null | "format";
  phone: boolean | null;
  password: boolean | null;
  confirmPassword: boolean | null | "notmatch";
};

interface LogedInProps {
  VehicleReservationMain: VehicleReservationMainProps;
  setVehicleReservationValue: (key: keyof VehicleReservationMainProps, value: any) => void;
}

interface NotLogedInProps {
  formMode: FormMode;
  setFormMode: (mode: FormMode) => void;
  signUpFormData: SignUpFormValues;
  signInFormData: SignInFormValues;
  validation: FormValidation;
  loading: boolean;
  handleSignUpInputChange: (field: keyof SignUpFormValues) => (event: any) => void;
  handleSignInInputChange: (field: keyof SignInFormValues) => (event: any) => void;
  handleSignUp: () => void;
  handleSignIn: () => void;
  handleResetPassword: () => void;
  resetEmail: string;
  resetEmailError: boolean | null | "format";
  handleResetEmailChange: (event: any) => void;
  getFieldError: (field: keyof FormValidation) => string;
  fieldStyles: any;
  getKey: (keyName: string, fallback?: string) => string;
}

const LogedIn: React.FC<LogedInProps> = ({ VehicleReservationMain, setVehicleReservationValue }) => {
  return (
    <Box sx={{ padding: "8px 16px", flex: 1, overflow: "auto" }}>
      {/* Login Status */}
      <LogInAs sx={{ mb: 3 }} />

      {/* Address Section */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "20px", marginBottom: "6px" }}>
          {" "}
          {VehicleReservationMain.orderType === "delivery"
            ? "Delivery Address"
            : "Pickup Address"}
        </Typography>

        {/* Display selected address */}
        {(VehicleReservationMain.selectedAddress ||
          VehicleReservationMain.manual_address) && (
          <Typography
            sx={{
              marginTop: "20px",
              fontWeight: 700,
              fontSize: "20px",
              mb: 2,
            }}
          >
            {VehicleReservationMain.use_manual
              ? VehicleReservationMain.manual_address || " "
              : VehicleReservationMain.selectedAddress &&
                `${VehicleReservationMain.selectedAddress.number ?? ""} ${
                  VehicleReservationMain.selectedAddress.street ?? ""
                }${
                  VehicleReservationMain.selectedAddress.property_name
                    ? `${VehicleReservationMain.selectedAddress.property_name}`
                    : ""
                }, ${VehicleReservationMain.selectedAddress.plantation ?? ""}`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const NotLogedIn: React.FC<NotLogedInProps> = ({
  formMode,
  setFormMode,
  signUpFormData,
  signInFormData,
  validation,
  loading,
  handleSignUpInputChange,
  handleSignInInputChange,
  handleSignUp,
  handleSignIn,
  handleResetPassword,
  resetEmail,
  resetEmailError,
  handleResetEmailChange,
  getFieldError,
  fieldStyles,
  getKey,
}) => {
  const { getKeyItem } = useManageKeys();
  if (formMode === "reset") {
    return (
      <Box sx={{ padding: "8px 16px", flex: 1, overflow: "auto" }}>
        <Typography
          variant="h5"
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            color: "#333",
            marginBottom: "8px",
          }}
        >
          Reset Password
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#666", mb: 3 }}>
          We will send you a password reset link to your email.
        </Typography>

        {/* Email Field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Email"
            type="email"
            value={resetEmail}
            onChange={handleResetEmailChange}
            error={resetEmailError === false || resetEmailError === "format"}
            helperText={
              resetEmailError === false
                ? "Email is required"
                : resetEmailError === "format"
                ? "Invalid email format"
                : ""
            }
            variant="standard"
            sx={fieldStyles}
          />
        </Box>

        {/* Submit Button */}
        <Box sx={{ mb: 2 }}>
          <LoadingButton
            fullWidth
            variant="contained"
            loading={loading}
            onClick={handleResetPassword}
            sx={{
              backgroundColor: "#0047C0",
              color: "white",
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#003a9b",
              },
            }}
          >
            Confirm
          </LoadingButton>
        </Box>

        <Link
          component="button"
          type="button"
          onClick={() => setFormMode("signin")}
          sx={{ fontSize: "14px", color: "#0047C0" }}
        >
          Back to Sign In
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "8px 16px", flex: 1, overflow: "auto" }}>
      {/* Mode Toggle */}
      <Box
        sx={{
          display: "flex",
          mb: 3,
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        <Button
          onClick={() => setFormMode("signin")}
          sx={{
            flex: 1,
            py: 1.5,
            backgroundColor:
              formMode === "signin" ? "#0047C0" : "transparent",
            color: formMode === "signin" ? "white" : "#0047C0",
            borderRadius: 0,
            "&:hover": {
              backgroundColor: formMode === "signin" ? "#0047C0" : "#f5f5f5",
            },
          }}
        >
          Sign In
        </Button>
        <Button
          onClick={() => setFormMode("signup")}
          sx={{
            flex: 1,
            py: 1.5,
            backgroundColor:
              formMode === "signup" ? "#0047C0" : "transparent",
            color: formMode === "signup" ? "white" : "#0047C0",
            borderRadius: 0,
            "&:hover": {
              backgroundColor: formMode === "signup" ? "#0047C0" : "#f5f5f5",
            },
          }}
        >
          Sign Up
        </Button>
      </Box>

      <Typography
        variant="h5"
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          color: "#333",
          marginBottom: "8px",
        }}
      >
        {formMode === "signin" ? "Sign In" : "Create Account"}
      </Typography>

      {formMode === "signup" && (
        <>
          {/* Name Fields */}
          <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="First Name"
              value={signUpFormData.firstName}
              onChange={handleSignUpInputChange("firstName")}
              error={validation.firstName === false}
              helperText={getFieldError("firstName")}
              variant="standard"
              sx={fieldStyles}
            />
            <TextField
              fullWidth
              placeholder="Last Name"
              value={signUpFormData.lastName}
              onChange={handleSignUpInputChange("lastName")}
              error={validation.lastName === false}
              helperText={getFieldError("lastName")}
              variant="standard"
              sx={fieldStyles}
            />
          </Box>

          {/* Phone Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Phone"
              value={signUpFormData.phone}
              onChange={handleSignUpInputChange("phone")}
              error={validation.phone === false}
              helperText={getFieldError("phone")}
              variant="standard"
              sx={fieldStyles}
            />
          </Box>
        </>
      )}

      {/* Email Field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Email"
          type="email"
          value={
            formMode === "signin"
              ? signInFormData.email
              : signUpFormData.email
          }
          onChange={
            formMode === "signin"
              ? handleSignInInputChange("email")
              : handleSignUpInputChange("email")
          }
          error={validation.email === false || validation.email === "format"}
          helperText={getFieldError("email")}
          variant="standard"
          sx={fieldStyles}
        />
      </Box>

      {/* Password Field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Password"
          type="password"
          value={
            formMode === "signin"
              ? signInFormData.password
              : signUpFormData.password
          }
          onChange={
            formMode === "signin"
              ? handleSignInInputChange("password")
              : handleSignUpInputChange("password")
          }
          error={validation.password === false}
          helperText={getFieldError("password")}
          variant="standard"
          sx={fieldStyles}
        />
      </Box>

      {/* Forgot Password Link (Sign In Only) */}
      {formMode === "signin" && (
        <Box sx={{ mb: 3, mt: -1, textAlign: "right" }}>
          <Link
            component="button"
            type="button"
            onClick={() => setFormMode("reset")}
            sx={{ fontSize: "14px", color: "#0047C0" }}
          >
            Forgot password?
          </Link>
        </Box>
      )}

      {/* Confirm Password Field (Sign Up Only) */}
      {formMode === "signup" && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Confirm Password"
            type="password"
            value={signUpFormData.confirmPassword}
            onChange={handleSignUpInputChange("confirmPassword")}
            error={
              validation.confirmPassword === false ||
              validation.confirmPassword === "notmatch"
            }
            helperText={getFieldError("confirmPassword")}
            variant="standard"
            sx={fieldStyles}
          />
        </Box>
      )}

      {/* SMS Opt-in Checkbox (Sign Up Only) */}
      {formMode === "signup" && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={signUpFormData.smsOptIn}
                onChange={handleSignUpInputChange("smsOptIn")}
                sx={{
                  color: "#0047C0",
                  "&.Mui-checked": {
                    color: "#0047C0",
                  },
                  mr: 1,
                  p: 0,
                }}
              />
            }
            label={
              <Typography
                sx={{ fontSize: "1rem", color: "#333", fontWeight: 500 }}
              >
                {getKey('you_will_receive_sms_confirmation_of_your_booking', 'You will receive SMS confirmation of your booking. Please opt in to receive additional reminders and updates')}
              </Typography>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
          />
        </Box>
      )}

      {/* Submit Button */}
      <Box sx={{ mb: 3 }}>
        <LoadingButton
          fullWidth
          variant="contained"
          loading={loading}
          onClick={formMode === "signin" ? handleSignIn : handleSignUp}
          sx={{
            backgroundColor: "#0047C0",
            color: "white",
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#003a9b",
            },
          }}
        >
          {formMode === "signin" ? "Sign In" : "Create Account"}
        </LoadingButton>
      </Box>

      {/* Terms and Privacy Disclaimer (Sign Up Only) */}
      {formMode === "signup" && (
        <Box sx={{ mt: 1, mb: 3 }}>
          <Editable keyName="by_opting_into_text_messages_you" fieldType="html" label="SMS Consent Text" value={getKeyItem('by_opting_into_text_messages_you')?.value || ''}>
            <Typography
              variant="body2"
              sx={{
                color: "#999",
                lineHeight: 1.5,
                fontSize: "0.9rem",
              }}
              dangerouslySetInnerHTML={{
                __html: getKeyItem('by_opting_into_text_messages_you')?.value || getKey(
                  'by_opting_into_text_messages_you',
                  'By opting into text messages, you: (i) expressly consent to be contacted by Carolina Rides or our agents for any business purpose at the telephone number(s) you provide, including for marketing or promotional messages; (ii) agree we may contact you using an automatic texting system. Opt out at any time by replying &ldquo;STOP&rdquo;. Message and data rates may apply. View our <a href="/terms" target="_blank" style="color: #999; text-decoration: underline;">Terms</a> and <a href="/privacy" target="_blank" style="color: #999; text-decoration: underline;">Privacy Policy</a>.'
                )
              }}
            />
          </Editable>
        </Box>
      )}
    </Box>
  );
};
const InfoStep: React.FC<InfoStepProps> = ({ sx }) => {
  const { next, back } = useStepper();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { setVehicleReservationValue, VehicleReservationMain, VehicleReservationItems } = useVehicleReservation();
  const { storeDetails, storeContent } = useStoreDetails();
  const { getKey } = useManageKeys();

  const [formMode, setFormMode] = useState<FormMode>("signup");
  const [loading, setLoading] = useState(false);

  const [signUpFormData, setSignUpFormData] = useState<SignUpFormValues>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    smsOptIn: false,
  });

  const [signInFormData, setSignInFormData] = useState<SignInFormValues>({
    email: "",
    password: "",
  });

  const [resetEmail, setResetEmail] = useState<string>("");
  const [resetEmailError, setResetEmailError] = useState<
    boolean | null | "format"
  >(null);

  const [validation, setValidation] = useState<FormValidation>({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    password: null,
    confirmPassword: null,
  });

  // Address search state variables
  const [expandSearchBox, setExpandSearchBox] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>("");
  const [searchedAddresses, setSearchedAddresses] = useState<Array<any>>([]);
  const [addressError, setAddressError] = useState(false);

  const isEmailValid = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (
      !VehicleReservationMain.pickup ||
      !VehicleReservationMain.dropoff ||
      !VehicleReservationItems.length
    ) {
      navigate("/rental");
    }
    if (!VehicleReservationMain.email && localStorage.getItem("customer_email"))
      setVehicleReservationValue("email", localStorage.getItem("customer_email"));
    if (
      !VehicleReservationMain.phone_number &&
      localStorage.getItem("customer_phone_number")
    )
      setVehicleReservationValue(
        "phone_number",
        localStorage.getItem("customer_phone_number")
      );
    if (!VehicleReservationMain.name && localStorage.getItem("full-name"))
      setVehicleReservationValue("name", localStorage.getItem("full-name"));
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem("access-token");
    if (accessToken) {
      next();
    }
  }, []);

  useEffect(() => {
    if (searchKey) {
      searchAddress(searchKey, storeDetails.id, (jsonRes: any, status) => {
        if (status === 200 && Array.isArray(jsonRes)) {
          setSearchedAddresses(
            jsonRes.map((address, index) => ({
              ...address,
              label: `${address.number || ""} ${address.street || ""}, ${
                address.plantation || ""
              }${
                address.property_name ? ` - ${address.property_name}` : ""
              }<span style="display:none;">${index}</span>`,
            }))
          );
        } else {
          setSearchedAddresses([]);
        }
      });
    } else {
      setSearchedAddresses([]);
    }
  }, [searchKey, storeDetails.id]);

  const handleSignUpInputChange =
    (field: keyof SignUpFormValues) => (event: any) => {
      console.log("🚀 ~ handleSignUpInputChange ~ event:", event)

      const value =
        field === "smsOptIn" ? event.target.checked : event.target.value;
      setSignUpFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
      setValidation((prev) => ({
        ...prev,
        [field]: null,
      }));
    };

  const handleSignInInputChange =
    (field: keyof SignInFormValues) => (event: any) => {
      setSignInFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      setValidation((prev) => ({
        ...prev,
        [field]: null,
      }));
    };

  const validateSignUpForm = (): boolean => {
    let isValid = true;
    const updatedValidation = { ...validation };

    // Validate required fields
    (["firstName", "lastName", "phone", "password"] as const).forEach(
      (field) => {
        if (!signUpFormData[field]) {
          updatedValidation[field] = false;
          isValid = false;
        } else {
          updatedValidation[field] = true;
        }
      }
    );

    // Validate email
    if (!signUpFormData.email) {
      updatedValidation.email = false;
      isValid = false;
    } else if (!isEmailValid(signUpFormData.email)) {
      updatedValidation.email = "format";
      isValid = false;
    } else {
      updatedValidation.email = true;
    }

    // Validate confirm password
    if (!signUpFormData.confirmPassword) {
      updatedValidation.confirmPassword = false;
      isValid = false;
    } else if (signUpFormData.password !== signUpFormData.confirmPassword) {
      updatedValidation.confirmPassword = "notmatch";
      isValid = false;
    } else {
      updatedValidation.confirmPassword = true;
    }

    setValidation(updatedValidation);
    return isValid;
  };

  const validateSignInForm = (): boolean => {
    let isValid = true;
    const updatedValidation = { ...validation };

    (["email", "password"] as const).forEach((field) => {
      if (!signInFormData[field]) {
        updatedValidation[field] = false;
        isValid = false;
      } else {
        updatedValidation[field] = true;
      }
    });

    setValidation(updatedValidation);
    return isValid;
  };

  const handleSignUp = async () => {
    if (!validateSignUpForm()) return;

    setLoading(true);
    const formValues = {
      first_name: signUpFormData.firstName,
      last_name: signUpFormData.lastName,
      email: signUpFormData.email,
      phone_number: signUpFormData.phone,
      password: signUpFormData.password,
      confirm_password: signUpFormData.confirmPassword,
      home_address: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
    };

    await register(formValues, (jsonRes: any, status: any) => {
      setLoading(false);
      switch (status) {
        case 200:
          localStorage.setItem("access-token", jsonRes.refreshToken);
          localStorage.setItem("full-name", jsonRes.fullName);
          localStorage.setItem("customerId", jsonRes.customerId);
          localStorage.setItem("customer_email", jsonRes.email);
          localStorage.setItem("customer_phone_number", jsonRes.phone_number);
          setVehicleReservationValue("email", jsonRes.email);
          setVehicleReservationValue("phone_number", jsonRes.phone_number);
          setVehicleReservationValue("name", jsonRes.fullName);
          enqueueSnackbar("Account created successfully!", {
            variant: "success",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          next();
          break;
        case 409:
          enqueueSnackbar("Email already exists", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
        default:
          enqueueSnackbar(jsonRes?.error || "Registration failed", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
      }
    });
  };

  const handleResetEmailChange = (event: any) => {
    setResetEmail(event.target.value);
    setResetEmailError(null);
  };

  const handleResetPassword = async () => {
    let isValid = true;
    if (!resetEmail) {
      setResetEmailError(false);
      isValid = false;
    } else if (!isEmailValid(resetEmail)) {
      setResetEmailError("format");
      isValid = false;
    } else {
      setResetEmailError(true);
    }
    if (!isValid) return;

    setLoading(true);
    const host = new URL(window.location.href).origin;
    const payload = {
      email: resetEmail,
      clientHost: host,
    };
    await resetpass(payload, (jsonRes: any, status: any) => {
      setLoading(false);
      switch (status) {
        case 200:
          enqueueSnackbar("Password reset link sent to your email", {
            variant: "success",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          setResetEmail("");
          setResetEmailError(null);
          setFormMode("signin");
          break;
        case 404:
          enqueueSnackbar(jsonRes?.message || "User not found", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
        case 500:
          enqueueSnackbar("Server Error", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
        default:
          enqueueSnackbar(jsonRes?.error || "Unknown Error", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
      }
    });
  };

  const handleSignIn = async () => {
    if (!validateSignInForm()) return;

    setLoading(true);
    await logIn(signInFormData, (jsonRes: any, status: any) => {
      setLoading(false);
      switch (status) {
        case 200:
          localStorage.setItem("access-token", jsonRes.refreshToken);
          localStorage.setItem("full-name", jsonRes.fullName);
          localStorage.setItem("customerId", jsonRes.customerId);
          localStorage.setItem("customer_email", jsonRes.email);
          localStorage.setItem("customer_phone_number", jsonRes.phone_number);
          setVehicleReservationValue("email", jsonRes.email);
          setVehicleReservationValue("phone_number", jsonRes.phone_number);
          setVehicleReservationValue("name", jsonRes.fullName);
          enqueueSnackbar("Logged in successfully!", {
            variant: "success",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          next();
          break;
        case 403:
          enqueueSnackbar("Incorrect password", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
        case 404:
          enqueueSnackbar("User not found", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
        default:
          enqueueSnackbar(jsonRes?.error || "Login failed", {
            variant: "error",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
          break;
      }
    });
  };

  const getFieldError = (field: keyof FormValidation) => {
    const error = validation[field];
    if (error === false)
      return `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    if (error === "format") return "Invalid email format";
    if (error === "notmatch") return "Passwords do not match";
    return "";
  };

  // Check if user is logged in
  const accessToken = localStorage.getItem("access-token");

  const onComplete = () => next();

  const isContinueButtonVisible: boolean = accessToken ? true : false;

  return (
    <Box>
      <StepperLayout
        rightContent={
          <Box>
            <SummaryCard
              isCheckoutStage={false}
              showDiscount={false}
            />
          </Box>
        }
      >
        <RentalHeader
          title="Carolina Rides"
          onActionClick={() => back()}
          primaryColor={storeContent.primary_color}
        />
        <Box sx={styles.contentContainer}>
          <Typography sx={styles.sectionTitle} variant="h5">
            {getKey('info_step_title', 'Your Reservation Info')}
          </Typography>
          {accessToken ? (
            <LogedIn 
              VehicleReservationMain={VehicleReservationMain}
              setVehicleReservationValue={setVehicleReservationValue}
            />
          ) : (
            <NotLogedIn
              formMode={formMode}
              setFormMode={setFormMode}
              signUpFormData={signUpFormData}
              signInFormData={signInFormData}
              validation={validation}
              loading={loading}
              handleSignUpInputChange={handleSignUpInputChange}
              handleSignInInputChange={handleSignInInputChange}
              handleSignUp={handleSignUp}
              handleSignIn={handleSignIn}
              handleResetPassword={handleResetPassword}
              resetEmail={resetEmail}
              resetEmailError={resetEmailError}
              handleResetEmailChange={handleResetEmailChange}
              getFieldError={getFieldError}
              fieldStyles={fieldStyles}
              getKey={getKey}
            />
          )}
        </Box>
      </StepperLayout>

      {/* Floating Continue Button */}
      <FloatingActionButton label="Continue" primaryColor={storeContent.primary_color} onClick={onComplete} visible={isContinueButtonVisible} disabled={!isContinueButtonVisible} />
    </Box>
  );
};

const fieldStyles = {
  "& .MuiInput-underline:before": {
    borderBottomColor: "#0047C0",
  },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottomColor: "#0047C0",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#0047C0",
  },
  "& .MuiInputBase-input": {
    fontSize: "1rem",
  },
};

const styles = {
  container: {
    display: "flex",
    flexDirection: {
      xs: "column",
      md: "row",
    },
  },
  contentContainer: {
    flex: 1,
    p: 1.5,
    backgroundColor: "white",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  contentTitle: {
    color: "#333333",
    fontSize: "16px",
  },
  sectionTitle: {
    color: "#333333",
    fontWeight: 700,
    fontSize: "16px",
    marginBottom: "8px",
  },
  infoContainer: {
    // width: matches900 ? "100%" : "auto",
    padding: "8px 16px",
    flex: 1,
    overflow: "auto",
  },
  infoTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "8px",
  },
};

export default InfoStep;
